/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {ActionButton, Badge, defaultTheme, DialogTrigger, Flex, Provider, ToggleButton} from '@adobe/react-spectrum';
import AIChat from './ai/icons/AIChat';
import Apps from '@spectrum-icons/workflow/Apps';
import Button from '@react/react-spectrum/Button';
import classNames from 'classnames';
import {FocusScope} from '@react-aria/focus';
import {getOrgDisplayName} from './utils/utils';
import Help from '@spectrum-icons/workflow/Help';
import {HelpPopover} from '@exc/help-center';
import {IntlProvider} from '@quarry/intl';
import * as localeDataPulse from './__localization__/ShellPulseComponents';
import More from '@spectrum-icons/workflow/More';
import {PulseDataProvider, PulseOverlayTrigger} from './ShellPulseComponents';
import React from 'react';
import Search from '@spectrum-icons/workflow/Search';
import ShellAppSwitcherPopover from './ShellAppSwitcherPopover';
import ShellHeaderUserProfile from './ShellUserProfile/ShellHeaderUserProfile';
import ShellOrgSwitcherPopover from './ShellOrgSwitcherPopover';
import ShellOverlayTrigger from './ShellOverlayTrigger';
import ShellSandboxSwitcherPopover from './ShellSandboxSwitcherPopover';
import ShellWait from './ShellWait';
import './ShellHeader.scss';
import './ShellHeaderActions.scss';
import {injectIntl} from 'react-intl';

class ShellHeaderActions extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      dialogOpen: false,
      showMoreActions: false
    };
  }

  /**
   * Shows feedback dialog.
   */
  showFeedbackTool = () => {
    const {feedback, solutionTitle} = this.props;

    switch (feedback.type) {
      case 'external':
        window.open(feedback.url, '_blank');
        break;
      case 'openFeedback':
        document.dispatchEvent(new CustomEvent('openHelpCenter', {
          detail: {
            config: {
              subject: feedback.subject || solutionTitle || 'Adobe Experience Cloud',
              type: 'BETA_FEEDBACK_SUBMISSION'
            },
            selectedTab: 'feedback'
          }
        }));
        break;
      default:
        return;
    }
  };

  /**
   * Toggles more actions.
   */
  toggleShowMoreActions = () => {
    this.setState({showMoreActions: !this.state.showMoreActions});
  };

  /**
   * Figures out tab index of the action buttons based on whether more actions button is present.
   * @returns {integer} the tab index.
   */
  actionsTabIndex = () => {
    const {showMoreActionsButton} = this.props;
    // If there is no button, tab as usual.
    // Otherwise do not allow tabbing through actions.
    let actionsTabIndex = showMoreActionsButton ? -1 : 0;
    if (showMoreActionsButton) {
      // If all the actions are showing after button is pressed, tab as usual.
      // Otherwise, block tabbing through them.
      actionsTabIndex = this.state.showMoreActions ? 0 : -1;
    }
    return actionsTabIndex;
  };

  /** All action components below */

  appSwitcherComponent = () => {
    const {
      appId,
      appRoot,
      closeOverlaysOnMenuShow,
      environment,
      intl,
      landingpage,
      locale,
      services,
      settingsData,
      settingsService,
      solutions
    } = this.props;
    const {formatMessage} = intl;

    return (
      <ShellOverlayTrigger
        closeOverlaysOnMenuShow={closeOverlaysOnMenuShow}
        key="app-switcher"
        placement="bottom left">
        <Button
          aria-label={formatMessage({
            defaultMessage: 'Switch to another Solution',
            description: 'Label for Solution Switcher',
            id: 'solutionSwitcher'})}
          className="app-switcher-menu"
          data-omega-element="app switcher"
          key="app-switcher-button"
          tabIndex={this.actionsTabIndex()}
          variant="tool">
          <Apps size="S" />
        </Button>
        <ShellAppSwitcherPopover
          aria-label={formatMessage({
            defaultMessage: 'Application list popover',
            description: 'Aria label for app switcher popover',
            id: 'solutionSwitcherPopover'})}
          currentAppId={appId}
          currentAppRoot={appRoot}
          environment={environment}
          landingpage={landingpage}
          linkOutAriaLabel={formatMessage({
            defaultMessage: 'Opens in a new window',
            description: 'label for icon indicating that a link opens in a new window',
            id: 'appSwitcherLinkOutIconLabel'})}
          listAriaLabel={formatMessage({
            defaultMessage: 'Application list',
            description: 'Aria label for app switcher list',
            id: 'solutionSwitcherList'})}
          locale={locale}
          services={services}
          settingsData={settingsData}
          settingsService={settingsService}
          solutions={solutions}
        />
      </ShellOverlayTrigger>
    );
  };

  customSearchComponent = () => {
    const {
      customSearch,
      intl
    } = this.props;
    const {formatMessage} = intl;
    return (
      <span
        className={classNames({'spectrum-Shell-custom-menu--open': customSearch.open})}
        key="custom-search"
        tabIndex={this.actionsTabIndex()}
      >
        <Button
          aria-label={formatMessage({
            defaultMessage: 'Search',
            description: 'Label for search button',
            id: 'searchButton'})}
          data-omega-element="custom search"
          icon={<Search />}
          key="custom-search-button"
          onClick={customSearch.onClick}
          quiet
          selected={customSearch.open}
          tabIndex={this.actionsTabIndex()}
          variant="tool" />
      </span>
    );
  };

  helpCenterComponent = () => {
    const {
      closeOverlaysOnMenuShow,
      helpCenterConfig,
      helpCenterProps,
      internal,
      intl,
      locale,
      onToggleHelpCenter,
      theme,
      toastsSupressedCount,
      translationService
    } = this.props;

    return (
      <ShellOverlayTrigger
        closeOverlaysOnMenuShow={closeOverlaysOnMenuShow}
        key="help-menu"
        onHide={e => onToggleHelpCenter(e, true)}
        placement="bottom"
        show={helpCenterProps && helpCenterProps.open}
        trigger="click">
        <Button
          aria-label={intl.formatMessage({
            defaultMessage: 'Help',
            description: 'Help header',
            id: 'help'})}
          className="help-menu"
          data-omega-element="help center"
          key="help-menu-button"
          onClick={onToggleHelpCenter}
          tabIndex={this.actionsTabIndex()}
          variant="tool">
          <Help size="S" />
        </Button>
        <HelpPopover
          {...helpCenterProps || helpCenterConfig}
          ShellWait={ShellWait}
          internal={internal}
          locale={locale}
          theme={theme}
          toastsSupressedCount={toastsSupressedCount}
          translationService={translationService} />
      </ShellOverlayTrigger>
    );
  };

  pulseComponent = () => {
    const {
      closeOverlaysOnMenuShow,
      environment,
      noAccess,
      pulse,
      theme
    } = this.props;

    return (
      <IntlProvider messages={localeDataPulse}>
        <PulseDataProvider pulse={pulse}>
          <PulseOverlayTrigger
            closeOverlaysOnMenuShow={closeOverlaysOnMenuShow}
            environment={environment}
            hasAccess={!noAccess}
            key="pulse-overlay"
            tabIndex={this.actionsTabIndex()}
            theme={theme} />
        </PulseDataProvider>
      </IntlProvider>
    );
  };

  userProfileComponent = () => (
    <ShellHeaderUserProfile props={this.props} tabIndex={this.actionsTabIndex()} />
  );

  sandboxSwitcherComponent = () => {
    const {
      closeOverlaysOnMenuShow,
      locale,
      recentSandboxes,
      sandbox,
      userRecentSandboxesHandler
    } = this.props;

    const sandboxLabel = (
      <Button
        className="spectrum-Shell-sandboxButton"
        data-omega-element="sandbox switcher"
        disabled={sandbox.sandboxes.length === 1}
        tabIndex={sandbox.sandboxes.length === 1 ? -1 : 0}
        variant="tool"
      >
        <span className="spectrum-Shell-sandboxButton-text">
          {`${sandbox.selected.title} (${sandbox.selected.region})`}
        </span>
        <Badge
          UNSAFE_className="spectrum-Shell-sandboxEnvLabel"
          variant={sandbox.selected.type === 'production' ? 'info' : 'neutral'}
        >
          {sandbox.selected.type === 'production' ? 'Prod' : 'Dev'}
        </Badge>
      </Button>
    );

    return sandbox.sandboxes.length > 1 ? (
      <ShellOverlayTrigger closeOverlaysOnMenuShow={closeOverlaysOnMenuShow} key="sandbox-menu" placement="bottom">
        {sandboxLabel}
        <ShellSandboxSwitcherPopover
          className="spectrum-Shell-sandboxSwitcher-popover"
          locale={locale}
          onSandboxChange={sandbox.onChange}
          recentSandboxes={recentSandboxes}
          sandboxes={sandbox.sandboxes}
          selectedSandbox={sandbox.selected}
          userRecentSandboxesHandler={userRecentSandboxesHandler}
        />
      </ShellOverlayTrigger>
    ) : sandboxLabel;
  };

  orgSwitcherComponent = () => {
    const {
      closeOverlaysOnMenuShow,
      hasAccountClusterData,
      hasSandboxBar,
      imsOrgs,
      intl,
      locale,
      noAccess,
      onImsOrgChange,
      orgToSubOrgsMap,
      selectedImsOrg,
      selectedSubOrg,
      subOrgAccessOnlyOnCurrentOrg
    } = this.props;
    const switchOrgLabel = intl.formatMessage({
      defaultMessage: 'Select another org account',
      description: 'Org switcher item to select another account',
      id: 'adobeExperienceCloudNoOrgsSelect'
    });
    const orgRef = React.createRef();
    return (
      <Provider theme={defaultTheme}>
        <DialogTrigger
          hideArrow
          key="org-menu"
          onOpenChange={isOpen => {
            // This can be removed once all of shell header is using V3
            isOpen && closeOverlaysOnMenuShow(orgRef);
          }}
          placement="bottom"
          ref={orgRef}
          type="popover"
        >
          <ActionButton
            UNSAFE_className="spectrum-Shell-orgButton"
            aria-label={switchOrgLabel}
            data-omega-element="org switcher"
            isQuiet
          >
            {noAccess ?
              switchOrgLabel :
              getOrgDisplayName(imsOrgs, selectedImsOrg, selectedSubOrg, orgToSubOrgsMap)}
          </ActionButton>
          {close =>
            <ShellOrgSwitcherPopover
              closeMenu={close}
              hasAccountClusterData={hasAccountClusterData}
              hasSandboxBar={hasSandboxBar}
              imsOrgs={imsOrgs}
              locale={locale}
              onImsOrgChange={onImsOrgChange}
              orgToSubOrgsMap={orgToSubOrgsMap}
              selectedImsOrg={selectedImsOrg}
              selectedSubOrg={selectedSubOrg}
              subOrgAccessOnlyOnCurrentOrg={subOrgAccessOnlyOnCurrentOrg}
            />
          }
        </DialogTrigger>
      </Provider>
    );
  };

  feedbackButtonComponent = () => {
    const {feedback, intl, helpCenterConfig} = this.props;
    const defaultLabel = intl.formatMessage({
      defaultMessage: 'Feedback',
      description: 'Default label for feedback button',
      id: 'feedback button default'});
    const label = feedback?.buttonLabel || defaultLabel;
    return (
      <Button
        aria-label={label}
        className="spectrum-Shell-feedback-button"
        data-omega-attribute-label={label}
        data-omega-attribute-type={feedback?.type}
        data-omega-element="feedback"
        key="feedback-button"
        onClick={this.showFeedbackTool}
        variant={feedback?.buttonVariant || helpCenterConfig?.feedbackConfig?.buttonVariant || 'cta'}
      >
        <span className="spectrum-Shell-feedback-button">
          {label}
        </span>
      </Button>
    );
  };

  showMoreActionsButtonComponent = tabIndex => (
    <Button
      aria-label={this.props.intl.formatMessage({
        defaultMessage: 'More Actions',
        description: 'More actions button',
        id: 'more actions'})}
      autoFocus={this.state.showMoreActions}
      className={`more-actions-button ${this.state.showMoreActions ? 'active' : ''}`}
      data-omega-element="more actions"
      key="more-actions-button"
      onClick={this.toggleShowMoreActions}
      tabIndex={tabIndex || 0}
      variant="tool">
      <More size="S" />
    </Button>
  );

  /** Logic for showing and hiding action components below */

  includeSandboxSwitcher = () => {
    const {sandbox} = this.props;
    // Include the sandbox switcher when sandboxes are enabled
    // However, if sandboxes are in incognito mode, do not show switcher
    return !sandbox?.incognito && sandbox?.enabled && sandbox?.sandboxes?.length;
  };

  includeOrgSwitcher = () => {
    const {imsOrgs, noAccess, orgToSubOrgsMap, selectedImsOrg, size} = this.props;

    // Include the org switcher when:
    // 1. size is not xsmall
    // 2. we have > 1 orgs
    // 3. we have a selected org
    // 4. we have 1 org and > 1 sub orgs
    const hasDifferentAccount = imsOrgs.some(({value}) => value.indexOf('@AdobeOrg') === -1);
    const hasMultipleSubOrgs = orgToSubOrgsMap && imsOrgs[0] && orgToSubOrgsMap[imsOrgs[0].value] && orgToSubOrgsMap[imsOrgs[0].value].length > 1;
    return imsOrgs && (imsOrgs.length > 1 || hasDifferentAccount || hasMultipleSubOrgs) && (selectedImsOrg || noAccess) && size !== 'XS';
  };

  includeFeedbackButton = () => this.props.feedback && this.props.feedback.enabled;

  sidebarComponent = () => {
    const {aiToggleRef, intl, onToggleSidebar, sidebarOpen} = this.props;

    return (
      <ToggleButton
        aria-label={intl.formatMessage({
          defaultMessage: 'Launch sidebar',
          description: 'Launch sidebar button',
          id: 'launchsidebar'})}
        data-omega-element="launch ai sidbar"
        isDisabled={this.actionsTabIndex() === -1}
        isQuiet
        isSelected={sidebarOpen}
        key="launch-sidebar-button"
        onPress={() => onToggleSidebar()}
        ref={aiToggleRef}>
        <AIChat />
      </ToggleButton>
    );
  };

  renderDefaultActions = () => {
    const {
      aiSidebarEnabled,
      checkAndReturnUnifiedSearch,
      customSearch,
      noAccess,
      showCollapsedSearch,
      showMoreActionsButton,
      unifiedSearchProps
    } = this.props;
    const {showMoreActions} = this.state;

    // Stores all actions except the Show More Actions button.
    const actions = [
      noAccess || this.appSwitcherComponent(),
      customSearch.enabled && this.customSearchComponent(),
      ((showMoreActionsButton && showMoreActions) || !showMoreActionsButton) && showCollapsedSearch && checkAndReturnUnifiedSearch({...unifiedSearchProps, tabIndex: this.actionsTabIndex()}, 'actions'),
      noAccess || this.helpCenterComponent(),
      aiSidebarEnabled && this.sidebarComponent(),
      this.pulseComponent(),
      this.userProfileComponent()
    ];

    if (this.includeSandboxSwitcher()) {
      actions.unshift(this.sandboxSwitcherComponent());
    }

    if (this.includeOrgSwitcher()) {
      actions.unshift(this.orgSwitcherComponent());
    }

    if (this.includeFeedbackButton()) {
      actions.unshift(this.feedbackButtonComponent());
    }

    return actions;
  };

  openPopover = e => {
    if (e.type === 'keydown' && (e.key !== 'Enter' && e.key !== ' ')) {
      return;
    }
    this.setState({dialogOpen: true});
  };

  focusHandler = e => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      this.setState({dialogOpen: false});
    }
  };

  renderActionsWithButton = () => {
    const {
      customSearch,
      renderEnvLabel,
      renderImpersonationLabel,
      renderReleaseBadge,
      sideNavEnabled
    } = this.props;
    const {showMoreActions} = this.state;

    // Stores the actions that show when
    // showMoreActionsButton = true & showMoreActions = false.
    const lessActions = [];

    if (this.includeSandboxSwitcher()) {
      lessActions.unshift(this.sandboxSwitcherComponent());
    }

    if (this.includeOrgSwitcher()) {
      lessActions.unshift(this.orgSwitcherComponent());
    }

    const secondActionsBar = (
      <Flex
        UNSAFE_className="second-actions-bar"
        alignItems="center"
        gap="size-100"
        justifyContent="end">
        {renderImpersonationLabel()}
        {renderReleaseBadge()}
        {renderEnvLabel()}
        {this.feedbackButtonComponent()}
      </Flex>
    );

    // Calculate starting index to create moreActions array based on actions present since
    // org switcher, sandbox switcher, and feedback button are not part of More Actions.
    const startIndex = [this.includeOrgSwitcher(), this.includeSandboxSwitcher(), this.includeFeedbackButton()]
      .filter(included => included).length;

    // Stores the actions that show when
    // showMoreActionsButton = true & showMoreActions = true.
    const moreActions = this.renderDefaultActions().slice(startIndex)
      .filter(action => action && action.key !== 'custom-search');

    lessActions.push(customSearch.enabled && this.customSearchComponent());
    lessActions.push(this.showMoreActionsButtonComponent());
    // The button in the more actions overlay requires special tab index handling.
    moreActions.push(this.showMoreActionsButtonComponent(this.actionsTabIndex()));

    const focusScopeContainer = children => (
      showMoreActions ? (
        <FocusScope contain={!this.state.dialogOpen} restoreFocus>
          {children}
        </FocusScope>
      ) : children
    );

    return (
      <>
        {lessActions}
        <div
          className={classNames('show-more-actions-bars-container', {invisible: !showMoreActions})}
          onClick={this.openPopover}
          onFocus={this.focusHandler}
          onKeyDownCapture={this.openPopover}
          role="none"
        >
          {focusScopeContainer(<div className={classNames('show-more-actions-container', {sideNavEnabled})}>{moreActions}</div>)}
          {this.includeFeedbackButton() ? secondActionsBar : null}
        </div>
      </>
    );
  };

  render () {
    return this.props.showMoreActionsButton ? this.renderActionsWithButton() : this.renderDefaultActions();
  }
}

const I18nShellHeaderActions = injectIntl(ShellHeaderActions);

export default I18nShellHeaderActions;
